import { ILegalClient } from '../Interfaces/ILegalClient';
import {
  ClientWarning,
  classifyClientReachability,
  fullName,
  joinNames,
  portalBlockReason,
} from '../helper/clientEmailStatus';

/**
 * Ein Mandant ist "zum Empfang ausgewählt", wenn für ihn eine Rechnungs- oder
 * Urkunden-Erinnerung aktiviert ist. Nur solche Mandanten werden gewarnt.
 */
const isSelectedForReminding = (client: ILegalClient) =>
  !!client.enableInvoiceReminding || !!client.enableCertificateReminding;

/**
 * Ermittelt die Warnungen für die Mandanten-Übersicht eines Vorgangs (#1198).
 * Betrachtet nur Mandanten mit aktiver Erinnerung (Rechnung/Urkunden) und
 * unterscheidet drei sich gegenseitig ausschließende Zustände:
 *
 *  - Fall 1 (rot):    keine E-Mail-Adresse -> weder Portal-Login noch
 *                     E-Mail-Benachrichtigung möglich (schwerwiegendster Fall).
 *  - Fall 3 (rot):    E-Mail vorhanden, aber kein Portalzugang -> Dokumente
 *                     können nicht geöffnet werden (mit ableitbarem Grund).
 *  - Fall 2 (orange): E-Mail vorhanden, Portalzugang möglich, aber E-Mail-
 *                     Empfang deaktiviert -> nur keine Benachrichtigung.
 *
 * @param clients die dem Vorgang zugeordneten Mandanten
 */
export const getClientReceptionWarnings = (
  clients: ILegalClient[]
): ClientWarning[] => {
  const relevant = clients.filter(isSelectedForReminding);
  const { noEmail, noPortal, noNotification } =
    classifyClientReachability(relevant);

  const warnings: ClientWarning[] = [];

  // Fall 1: keine E-Mail-Adresse hinterlegt (kritisch).
  if (noEmail.length > 0) {
    const plural = noEmail.length > 1;
    warnings.push({
      severity: 'error',
      text: plural
        ? `Für ${joinNames(noEmail)} sind keine E-Mail-Adressen hinterlegt. Diese Mandanten können sich daher nicht im Mandantenportal anmelden, erhalten keine Benachrichtigungen und haben keinen Zugriff auf bereitgestellte Dokumente. Bitte hinterlegen Sie eine E-Mail-Adresse oder deaktivieren Sie die Erinnerungen.`
        : `Für ${joinNames(noEmail)} ist keine E-Mail-Adresse hinterlegt. Der Mandant kann sich daher nicht im Mandantenportal anmelden, erhält keine Benachrichtigungen und hat keinen Zugriff auf bereitgestellte Dokumente. Bitte hinterlegen Sie eine E-Mail-Adresse oder deaktivieren Sie die Erinnerungen.`,
    });
  }

  // Fall 3: kein Portalzugang trotz vorhandener E-Mail (kritisch).
  if (noPortal.length > 0) {
    const plural = noPortal.length > 1;
    const entries = noPortal
      .map(client => `${fullName(client)} (${portalBlockReason(client)})`)
      .join(', ');
    warnings.push({
      severity: 'error',
      text: plural
        ? `Die folgenden Mandanten können sich derzeit nicht im Mandantenportal anmelden und haben daher keinen Zugriff auf bereitgestellte Dokumente: ${entries}. Bitte prüfen Sie den Zugang zum Mandantenportal.`
        : `${entries} kann sich derzeit nicht im Mandantenportal anmelden und hat daher keinen Zugriff auf bereitgestellte Dokumente. Bitte prüfen Sie den Zugang zum Mandantenportal.`,
    });
  }

  // Fall 2: E-Mail-Empfang deaktiviert, Portalzugang aber möglich (Warnung).
  if (noNotification.length > 0) {
    const plural = noNotification.length > 1;
    warnings.push({
      severity: 'warning',
      text: plural
        ? `${joinNames(noNotification)} können Dokumente im Mandantenportal abrufen, erhalten jedoch keine Benachrichtigungen per E-Mail. Bitte aktivieren Sie den Empfang von E-Mails.`
        : `${joinNames(noNotification)} kann Dokumente im Mandantenportal abrufen, erhält jedoch keine Benachrichtigungen per E-Mail. Bitte aktivieren Sie den Empfang von E-Mails.`,
    });
  }

  return warnings;
};
